import { useMemo } from 'react';
import { getSimpleEqQuery } from '../../../utils/queryFilters';
import useGetTrips from './useGetTrips';

const ONGOING_TRIPS_QUERY = `?limit=400&skip=0&sortField=startAt&sortOrder=DESC${getSimpleEqQuery('endAt', 'null', 0)}`;

/** Merged active-trip speed (same rules as TripsItem). `null` = no speed from active API — do not use GPS on the map. */
export type OngoingTripSpeedDisplayMap = Record<string, number | null>;
export type OngoingTripSpeedDisplayByVehicleId = OngoingTripSpeedDisplayMap;
export type OngoingTripSpeedDisplayByDeviceId = OngoingTripSpeedDisplayMap;

function useOngoingTripMapIds(fleetId: string): {
  ongoingVehicleIds: string[] | undefined;
  ongoingDeviceIds: string[] | undefined;
  ongoingTripSpeedDisplayByVehicleId: OngoingTripSpeedDisplayByVehicleId | undefined;
  ongoingTripSpeedDisplayByDeviceId: OngoingTripSpeedDisplayByDeviceId | undefined;
} {
  const { data, isSuccess } = useGetTrips(fleetId, ONGOING_TRIPS_QUERY, !!fleetId);
  return useMemo(() => {
    if (!isSuccess) {
      return {
        ongoingVehicleIds: undefined,
        ongoingDeviceIds: undefined,
        ongoingTripSpeedDisplayByVehicleId: undefined,
        ongoingTripSpeedDisplayByDeviceId: undefined,
      };
    }
    const vehicles = new Set<string>();
    const devices = new Set<string>();
    const ongoingTripSpeedDisplayByVehicleId: OngoingTripSpeedDisplayByVehicleId = {};
    const ongoingTripSpeedDisplayByDeviceId: OngoingTripSpeedDisplayByDeviceId = {};
    data?.results?.forEach((trip: any) => {
      const vid = trip?.vehicle?.vehicleId;
      if (typeof vid === 'string') vehicles.add(vid);
      if (typeof trip?.deviceId === 'string') devices.add(trip.deviceId);
      if (trip?.endAt != null) return;
      const s = trip?.speedMs;
      const merged = typeof s === 'number' ? s : null;
      if (typeof vid === 'string') {
        ongoingTripSpeedDisplayByVehicleId[vid] = merged;
      }
      if (typeof trip?.deviceId === 'string') {
        ongoingTripSpeedDisplayByDeviceId[trip.deviceId] = merged;
      }
    });
    return {
      ongoingVehicleIds: Array.from(vehicles),
      ongoingDeviceIds: Array.from(devices),
      ongoingTripSpeedDisplayByVehicleId,
      ongoingTripSpeedDisplayByDeviceId,
    };
  }, [isSuccess, data?.results]);
}

export default useOngoingTripMapIds;
